import { Building, Eye, Edit3, Trash2, MapPin, DollarSign, Calendar } from 'lucide-react'
import { formatCurrency } from '../utils/formatCurrency'

interface Imovel {
    id: string
    nome: string
    endereco: string | null
    estado: string | null
    cidade: string | null
    tipo: string
    tipo_imovel: string
    valor_venda: number | null
    construtora_id: string | null
    unidades_disponiveis: number
    unidades_vendidas: number
    created_at: string
    construtora?: {
        nome: string
        cnpj: string
        contato_responsavel: string
    }
    unidades_count?: number
}

interface ImoveisCardViewProps {
    imoveis: Imovel[]
    onView: (imovel: Imovel) => void
    onEdit: (imovel: Imovel) => void
    onDelete: (imovel: Imovel) => void
    formatDate: (date: string) => string
}

export default function ImoveisCardView({
    imoveis,
    onView,
    onEdit,
    onDelete,
    formatDate
}: ImoveisCardViewProps) {
    if (imoveis.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <Building className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Nenhum imóvel encontrado</h3>
                <p className="text-gray-500 dark:text-gray-400">
                    Comece criando seu primeiro imóvel
                </p>
            </div>
        )
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {imoveis.map((imovel) => (
                <div
                    key={imovel.id}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow duration-200"
                >
                    {/* Header do Card */}
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1 line-clamp-2">
                                    {imovel.nome}
                                </h3>
                                {(imovel.estado || imovel.cidade) && (
                                    <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 mb-2">
                                        <MapPin className="w-4 h-4 flex-shrink-0" />
                                        <span className="truncate">
                                            {imovel.cidade && imovel.estado
                                                ? `${imovel.cidade}, ${imovel.estado}`
                                                : imovel.cidade || imovel.estado || '-'}
                                        </span>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs font-medium text-gray-800 dark:text-gray-300">
                                        {imovel.tipo}
                                    </span>
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${imovel.tipo_imovel === 'novo'
                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                        : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                                        }`}>
                                        {imovel.tipo_imovel === 'novo' ? 'Novo' : 'Usado'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Conteúdo do Card */}
                    <div className="p-4">
                        {/* Construtora */}
                        {imovel.construtora && (
                            <div className="flex items-center mb-3">
                                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3">
                                    <Building className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                        {imovel.construtora.nome}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Valor */}
                        <div className="mb-3">
                            <div className="flex items-center gap-2 mb-1">
                                <DollarSign className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                <p className="text-xs text-gray-500 dark:text-gray-400">Valor de Venda</p>
                            </div>
                            <p className="text-xl font-bold text-green-600 dark:text-green-400">
                                {imovel.valor_venda ? formatCurrency(imovel.valor_venda) : 'N/A'}
                            </p>
                        </div>

                        {/* Stats de Unidades */}
                        <div className="grid grid-cols-2 gap-2 mb-4">
                            <div className="text-center p-2 bg-green-50 dark:bg-green-900/30 rounded">
                                <p className="text-xs text-gray-500 dark:text-gray-400">Vendidas</p>
                                <p className="text-sm font-bold text-green-600 dark:text-green-400">
                                    {imovel.unidades_vendidas || 0}
                                </p>
                            </div>
                            <div className="text-center p-2 bg-blue-50 dark:bg-blue-900/30 rounded">
                                <p className="text-xs text-gray-500 dark:text-gray-400">Disponíveis</p>
                                <p className="text-sm font-bold text-blue-600 dark:text-blue-400">
                                    {imovel.unidades_disponiveis || 0}
                                </p>
                            </div>
                        </div>

                        {/* Data de Cadastro */}
                        <div className="flex items-center gap-2 mb-4 text-xs text-gray-500 dark:text-gray-400">
                            <Calendar className="w-4 h-4 flex-shrink-0" />
                            <span>Cadastrado em {formatDate(imovel.created_at)}</span>
                        </div>

                        {/* Ações */}
                        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => onView(imovel)}
                                className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            >
                                <Eye className="w-4 h-4 inline mr-1" />
                                Ver
                            </button>
                            <button
                                onClick={() => onEdit(imovel)}
                                className="flex-1 px-3 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                            >
                                <Edit3 className="w-4 h-4 inline mr-1" />
                                Editar
                            </button>
                            <button
                                onClick={() => onDelete(imovel)}
                                className="px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}

